/**
 * @fileoverview `<PaymentProofActions>` — actions de preuve de paiement.
 *
 * Le portail remet UN document en direct : le justificatif de débit,
 * disponible dès `payment_status=PAID`. La quittance officielle n'est PAS
 * délivrée par le guichet (décision client, réunion du 16/07/2026) : elle
 * est émise par Quipux et se télécharge sur le compte Quipux de l'usager,
 * en dehors de ce système.
 *
 * Le bouton « Télécharger ma quittance » est conservé pour garder un
 * parcours évident côté citoyen : il ouvre une MODALE qui explique où se
 * trouve la quittance, puis redirige vers le compte Quipux (nouvel onglet).
 *
 * L'invariant financier demeure : le citoyen ne repart jamais sans preuve
 * (le justificatif de débit est remis immédiatement).
 *
 * @see Documents/Paymetrust_Guide_Integration_Frontend_v1.pdf §4.6
 */

import { Download, ExternalLink } from 'lucide-react';
import { Button } from './ui/button.tsx';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx';

/**
 * URL du compte Quipux de l'usager — système EXTERNE au GNPC.
 * Adresse de redirection provisoire fournie le 16/07/2026 (e-services CGI) ;
 * à confirmer avec Quipux avant mise en production.
 */
const QUIPUX_ACCOUNT_URL = 'https://eservices.cgi.ci/avit/#/public';

export interface PaymentProofActionsProps {
  /** Indique si le justificatif de débit peut être téléchargé. */
  debitNoteAvailable: boolean;
  /** Callback frontend pour le justificatif. */
  onDownloadDebitNote?: () => void;
  /** URL du compte Quipux — surchargable pour la recette/production. */
  quipuxAccountUrl?: string;
}

/**
 * Actions de preuve de paiement : téléchargement du justificatif de débit,
 * et quittance officielle via une modale de renvoi vers le compte Quipux.
 *
 * @param props - Disponibilité du justificatif, callback et URL Quipux.
 * @returns Les deux boutons de preuves, avec modale de redirection quittance.
 *
 * @example
 * ```tsx
 * <PaymentProofActions debitNoteAvailable />
 * ```
 */
export function PaymentProofActions({
  debitNoteAvailable,
  onDownloadDebitNote,
  quipuxAccountUrl = QUIPUX_ACCOUNT_URL,
}: PaymentProofActionsProps): React.JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Button type="button" size="lg" disabled={!debitNoteAvailable} onClick={onDownloadDebitNote}>
        <Download />
        Justificatif de débit
      </Button>

      {/* Le libellé reste orienté action (« Télécharger ma quittance »),
        * mais le clic ouvre une modale : la quittance étant délivrée HORS
        * système, on explique avant de rediriger — jamais de départ du
        * guichet sans que l'usager comprenne où il va. */}
      <Dialog>
        <DialogTrigger asChild>
          <Button
            type="button"
            size="lg"
            variant="outline"
            title="La quittance officielle se télécharge sur votre compte Quipux."
          >
            <Download />
            Télécharger ma quittance
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Votre quittance est sur votre compte Quipux</DialogTitle>
            <DialogDescription>
              La quittance officielle n'est pas délivrée par ce guichet : elle est établie par
              Quipux après l'enregistrement de votre paiement. Connectez-vous à votre compte
              Quipux pour la consulter et la télécharger. Votre justificatif de débit, lui, reste
              disponible ici.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Plus tard
              </Button>
            </DialogClose>
            <Button asChild>
              <a
                href={quipuxAccountUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Accéder à mon compte Quipux (ouvre un nouvel onglet)"
              >
                <ExternalLink />
                Accéder à mon compte Quipux
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
